import {
  Icon,
  List,
  useCallbackRef,
  useNavigationContext,
} from '@infotech-soe/erp-ui-kit';
import { tg } from '@infotech-soe/metaschema';
import classNames from 'classnames';
import React, {
  forwardRef,
  memo,
  MouseEvent,
  Ref,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { IPanelLink } from '../../../models/dataset/DatasetRelated';
import { useTextOverflowed } from '../../hooks/useTextOverflowed';
import { DRAGGABLE_EVENT_NAME } from '../MainView/PanelLinks';

import { ActionNames } from './ApplicationMenu';
import {
  DEFAULT_ACTION_ICON,
  DEFAULT_DISPLAY_MODE_ICON,
  MENU_ROOT_ICON_CLASS,
} from './constants';
import { useMenuContext } from './MenuContext';
import { useMenuItemStyles } from './styles';
import { MenuItemProps } from './types';
import { useDraggable } from './useDraggable';
import WithTooltip from './WithTooltip';

const MenuItem = (
  props: MenuItemProps,
  ref: Ref<HTMLDivElement>
): React.JSX.Element => {
  const { icon: iconProp, id, label, menuNestinglevel = 0 } = props;
  const classes = useMenuItemStyles(menuNestinglevel);
  const listItemRef = useRef(null);
  const optionsButtonRef = useRef(null);

  const { onDragEnd, onDragStart } = useDraggable(props);
  const { isOverflowed, textRef } = useTextOverflowed();
  const { active, onActivate, onRegister } = useNavigationContext();
  const {
    collapsed,
    isAlreadyOpen,
    onActivateOpen,
    onOpenNew,
    onSavePanelLink,
    onToggleMenu,
    setOptions,
  } = useMenuContext();

  const isActive = useMemo(() => active === id, [active, id]);

  const isAction = tg.hasProperty(props, 'action');

  const isCategoryOpen = useMemo(
    () => !isAction && isAlreadyOpen(props.category, props.displayMode),
    [isAlreadyOpen, isAction, props]
  );

  const handleSavePanelLink = useCallback(() => {
    const link: IPanelLink = {
      label,
      ...(iconProp && { icon: iconProp }),
      category: props.category,
      displayMode: props.displayMode,
      ...(props.inParams && { inParams: props.inParams }),
      ...(isAction ? { action: props.action } : {}),
    };
    onSavePanelLink(link);
  }, [iconProp, isAction, label, onSavePanelLink, props]);

  const handleActivateOpen = useCallback(() => {
    if (!isAction) onActivateOpen?.(props.category, props.displayMode);
  }, [isAction, onActivateOpen, props]);

  const handleOpenNew = useCallback(
    (withDefaultConfig = false) => {
      onOpenNew(
        props.category,
        props.displayMode,
        props.inParams,
        withDefaultConfig,
        isAction ? props.action : undefined
      );
    },
    [isAction, onOpenNew, props]
  );

  const handleListItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
      setOptions(opts => ({ ...opts, reference: null }));

      if (!isCategoryOpen || event.ctrlKey) handleOpenNew();
      else handleActivateOpen();

      if (!collapsed) onToggleMenu(false);
    },
    [
      collapsed,
      handleActivateOpen,
      handleOpenNew,
      isCategoryOpen,
      onToggleMenu,
      setOptions,
    ]
  );

  const handleContextMenuClick = useCallback(
    (actionName: ActionNames) => {
      const hanlers = {
        [ActionNames.ADD_TO_PANELLINKS]: handleSavePanelLink,
        [ActionNames.OPEN]: handleOpenNew,
        [ActionNames.ACTIVATE]: handleActivateOpen,
        [ActionNames.OPEN_DEFAULT]: () => handleOpenNew(true),
      };
      hanlers[actionName]();
      if (actionName !== ActionNames.ADD_TO_PANELLINKS) onToggleMenu(false);
    },
    [handleSavePanelLink, handleOpenNew, handleActivateOpen, onToggleMenu]
  );

  const handleContextMenu = useCallback(
    (reference: HTMLElement | null) => () =>
      setOptions?.({
        isCategoryOpen,
        reference,
        onContextMenuClick: handleContextMenuClick,
      }),
    [setOptions, isCategoryOpen, handleContextMenuClick]
  );

  const handleOptionsButtonClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      handleContextMenu(optionsButtonRef.current)();
    },
    [handleContextMenu]
  );

  const callbackRef = useCallbackRef(
    ref,
    listItemRef,
    (el: HTMLElement | null) => onRegister(el, id)
  );

  const icon = useMemo(() => {
    const defaultIcon = isAction
      ? DEFAULT_ACTION_ICON
      : DEFAULT_DISPLAY_MODE_ICON;
    return iconProp || (menuNestinglevel === 0 && defaultIcon);
  }, [isAction, iconProp, menuNestinglevel]);

  return (
    <List.Item
      className={classes.listItemContainer}
      data-draggable-name={DRAGGABLE_EVENT_NAME}
      draggable
      isActive={isActive}
      name={id}
      ref={callbackRef}
      tabIndex={isActive ? 0 : -1}
      onActivate={onActivate}
      onClick={handleListItemClick}
      onContextMenu={() => handleContextMenu(listItemRef.current)()}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
    >
      {icon && menuNestinglevel === 0 && (
        <Icon className={MENU_ROOT_ICON_CLASS} name={icon} />
      )}
      <WithTooltip condition={isOverflowed} label={label} delay={300}>
        <div className={classes.label} ref={textRef}>
          {label}
        </div>
      </WithTooltip>
      <Icon
        className={classNames(classes.optionsButton, 'optionsIcon')}
        name="all"
        ref={optionsButtonRef}
        size={12}
        onClick={handleOptionsButtonClick}
      />
    </List.Item>
  );
};

export default memo(forwardRef(MenuItem));
